import type { IEventStore } from "applesauce-core/event-store";
import type { Filter } from "applesauce-core/helpers";
import type { RelayPool } from "applesauce-relay";
import type { NostrEvent } from "nostr-tools";

import { requestPreWriteRelaySnapshot } from "@/lib/preWriteRelaySnapshot";
import { normalizeUrl } from "@/lib/url";
import {
  fallbackRelays,
  gitIndexRelays,
  lookupRelays,
} from "@/services/settings";

const CREATION_REFRESH_TIMEOUT_MS = 10_000;
const REPOSITORY_KINDS = [30_617, 30_618];

export interface RepositoryCreationFreshnessOptions {
  pool: RelayPool;
  store: IEventStore;
  pubkey: string;
  identifier: string;
  repositoryRelays: readonly string[];
  privateRepository?: boolean;
  maxSettleTime?: number;
}

function normalizedRelays(relays: readonly string[]): string[] {
  return [...new Set(relays.map(normalizeUrl))].sort();
}

function isDeletedBy(event: NostrEvent, deletions: readonly NostrEvent[]) {
  const dTag = event.tags.find(([name]) => name === "d")?.[1] ?? "";
  const coordinate = `${event.kind}:${event.pubkey}:${dTag}`;
  return deletions.some(
    (deletion) =>
      deletion.kind === 5 &&
      deletion.pubkey === event.pubkey &&
      deletion.created_at >= event.created_at &&
      deletion.tags.some(
        ([name, value]) =>
          (name === "e" && value === event.id) ||
          (name === "a" && value === coordinate),
      ),
  );
}

/** Prove a same-author repository address is unused before signing it. */
export async function assertRepositoryAddressAvailable({
  pool,
  store,
  pubkey,
  identifier,
  repositoryRelays,
  privateRepository = false,
  maxSettleTime = CREATION_REFRESH_TIMEOUT_MS,
}: RepositoryCreationFreshnessOptions): Promise<void> {
  const repoRelays = normalizedRelays(repositoryRelays);
  const indexRelays = privateRepository
    ? []
    : normalizedRelays(gitIndexRelays.getValue());
  const contributors = normalizedRelays([
    ...repoRelays,
    ...indexRelays,
    ...(privateRepository ? [] : fallbackRelays.getValue()),
    ...(privateRepository ? [] : lookupRelays.getValue()),
  ]);
  const announcementVoters = normalizedRelays([...repoRelays, ...indexRelays]);
  if (announcementVoters.length === 0 || repoRelays.length === 0) {
    throw new Error(
      "A selected repository relay is required to verify this repository address.",
    );
  }

  const deadline = Date.now() + maxSettleTime;
  const announcementCoordinate = `30617:${pubkey}:${identifier}`;
  const stateCoordinate = `30618:${pubkey}:${identifier}`;
  const [announcementRead, stateRead] = await Promise.all([
    requestPreWriteRelaySnapshot({
      pool,
      store,
      filters: [
        { kinds: [30_617], authors: [pubkey], "#d": [identifier] } as Filter,
        {
          kinds: [5],
          authors: [pubkey],
          "#a": [announcementCoordinate],
        } as Filter,
      ],
      contributorRelays: contributors,
      voterRelays: announcementVoters,
      requiredVotes: 1,
      maxSettleTime,
      voterLabel: "repository announcement or Git-index relay",
    }),
    requestPreWriteRelaySnapshot({
      pool,
      store,
      filters: [
        { kinds: [30_618], authors: [pubkey], "#d": [identifier] } as Filter,
        {
          kinds: [5],
          authors: [pubkey],
          "#a": [stateCoordinate],
        } as Filter,
      ],
      contributorRelays: contributors,
      voterRelays: repoRelays,
      requiredVotes: 1,
      maxSettleTime,
      voterLabel: "selected repository state relay",
    }),
  ]);
  const candidates = [
    ...new Map(
      [...announcementRead.events, ...stateRead.events].map((event) => [
        event.id,
        event,
      ]),
    ).values(),
  ];
  const announcementIds = candidates
    .filter(({ kind }) => kind === 30_617)
    .map(({ id }) => id);
  const stateIds = candidates
    .filter(({ kind }) => kind === 30_618)
    .map(({ id }) => id);
  const remaining = Math.max(0, deadline - Date.now());
  const [announcementDeletionRead, stateDeletionRead] = await Promise.all([
    announcementIds.length > 0
      ? requestPreWriteRelaySnapshot({
          pool,
          store,
          filters: [
            {
              kinds: [5],
              authors: [pubkey],
              "#e": announcementIds,
            } as Filter,
          ],
          contributorRelays: contributors,
          voterRelays: announcementRead.quorumRelays,
          requiredVotes: announcementRead.quorumRelays.length,
          maxSettleTime: remaining,
          voterLabel: "locked repository announcement relay",
        })
      : Promise.resolve({ events: [] }),
    stateIds.length > 0
      ? requestPreWriteRelaySnapshot({
          pool,
          store,
          filters: [
            { kinds: [5], authors: [pubkey], "#e": stateIds } as Filter,
          ],
          contributorRelays: contributors,
          voterRelays: stateRead.quorumRelays,
          requiredVotes: stateRead.quorumRelays.length,
          maxSettleTime: remaining,
          voterLabel: "locked repository state relay",
        })
      : Promise.resolve({ events: [] }),
  ]);
  const deletions = [
    ...candidates,
    ...announcementDeletionRead.events,
    ...stateDeletionRead.events,
  ].filter(({ kind }) => kind === 5);
  const collisions = candidates.filter(
    (event) =>
      REPOSITORY_KINDS.includes(event.kind) && !isDeletedBy(event, deletions),
  );
  if (collisions.length > 0) {
    const kinds = [...new Set(collisions.map(({ kind }) => kind))].join(
      " and ",
    );
    throw new Error(
      `Repository address ${identifier} is already used by an undeleted kind:${kinds} event from this account. Choose a different identifier.`,
    );
  }
}
